import { useEffect, useState } from 'react';
import { readProgress, classifyVocabTerm, getVocabMeta } from '../utils/progress.js';
import type { VerbStats } from '../utils/progress.js';
import { VocabularyIcon } from './VocabularyIcon.js';

const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

// ── Growth stages ─────────────────────────────────────────────────────────────
// 1 = seedling  (first correct recall)
// 2 = sprout    (3+ correct)
// 3 = sapling   (7+ correct or multiple days seen)
// 4 = tree      (mastered / known state)
type GrowthStage = 1 | 2 | 3 | 4;
type WaterStatus = 'fresh' | 'thirsty' | 'wilting';

const TENSE_FRUIT: Record<string, { color: string; label: string }> = {
  present:              { color: '#f97316', label: 'present' },
  preterite:            { color: '#ef4444', label: 'preterite' },
  imperfect:            { color: '#8b5cf6', label: 'imperfect' },
  future:               { color: '#3b82f6', label: 'future' },
  conditional:          { color: '#ec4899', label: 'conditional' },
  present_subjunctive:  { color: '#14b8a6', label: 'subjunctive' },
};

function getGrowthStage(stats: VerbStats): GrowthStage {
  const { correct, history } = stats;
  if (correct >= 1 && correct < 3) return 1;
  if (correct < 7) return 2;
  // Check if seen across multiple calendar days
  const days = new Set(history.map(h => new Date(h.timestamp).toDateString()));
  if (correct >= 7 && days.size >= 2) return 3;
  if (correct >= 12) return 4;
  return 2;
}

function getWaterStatus(lastSeen: number): WaterStatus {
  const days = (Date.now() - lastSeen) / 86_400_000;
  if (days > 7) return 'wilting';
  if (days > 3) return 'thirsty';
  return 'fresh';
}

function getTensesFruit(term: string): string[] {
  const store = readProgress();
  const tenses = new Set<string>();
  for (const [key, conj] of Object.entries(store.byConjugation)) {
    const [verb, tense] = key.split('|');
    if (verb === term && tense && conj.correctCount > 0 && TENSE_FRUIT[tense]) {
      tenses.add(tense);
    }
  }
  return Array.from(tenses);
}

// ── Plant SVG illustrations ───────────────────────────────────────────────────
function PlantSVG({ stage, water }: { stage: GrowthStage; water: WaterStatus }) {
  const wilt = water === 'wilting';
  const g = wilt ? '#7a9a6a' : '#4a8c3a'; // desaturate if wilting
  const g2 = wilt ? '#9aaa7a' : '#6ab84e';
  const br = '#8B6040';

  if (stage === 1) return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <ellipse cx="16" cy="26" rx="7" ry="2.5" fill={C.surf2} opacity="0.7"/>
      <line x1="16" y1="25" x2="16" y2="16" stroke={g} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M16 19 C16 19 11 16 11 11 C11 11 15 11 16 17" fill={g2} opacity={wilt ? 0.6 : 0.9}/>
      <path d="M16 18 C16 18 21 15 21 10 C21 10 17 10 16 16" fill={g} opacity={wilt ? 0.55 : 0.85}/>
    </svg>
  );

  if (stage === 2) return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <ellipse cx="16" cy="27" rx="8" ry="2" fill={C.surf2} opacity="0.6"/>
      <line x1="16" y1="26" x2="16" y2="14" stroke={g} strokeWidth="2" strokeLinecap="round"/>
      <path d="M16 22 C16 22 10 18 10 12 C10 12 15 12 16 20" fill={g2} opacity={wilt ? 0.6 : 0.9}/>
      <path d="M16 20 C16 20 22 16 22 10 C22 10 17 10 16 18" fill={g} opacity={wilt ? 0.55 : 0.85}/>
      <path d="M16 16 C16 16 12 13 13 9" stroke={g2} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
      <path d="M16 15 C16 15 20 12 19 8" stroke={g} strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
    </svg>
  );

  if (stage === 3) return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <ellipse cx="16" cy="28" rx="6" ry="1.8" fill={C.surf2} opacity="0.5"/>
      <rect x="14.5" y="18" width="3" height="10" rx="1.2" fill={br}/>
      <circle cx="16" cy="13" r="8" fill={g2} opacity={wilt ? 0.55 : 0.75}/>
      <circle cx="16" cy="11" r="6" fill={g} opacity={wilt ? 0.6 : 0.9}/>
      <circle cx="12" cy="14" r="4" fill={g2} opacity={wilt ? 0.5 : 0.7}/>
      <circle cx="20" cy="14" r="4" fill={g2} opacity={wilt ? 0.5 : 0.7}/>
    </svg>
  );

  // stage 4 — full tree
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <ellipse cx="16" cy="29" rx="7" ry="1.8" fill={C.surf2} opacity="0.5"/>
      <rect x="14" y="19" width="4" height="10" rx="1.5" fill={br}/>
      <circle cx="16" cy="12" r="10" fill={g2} opacity="0.65"/>
      <circle cx="16" cy="10" r="8" fill={g} opacity="0.9"/>
      <circle cx="11" cy="14" r="5" fill={g2} opacity="0.7"/>
      <circle cx="21" cy="14" r="5" fill={g2} opacity="0.7"/>
      <circle cx="16" cy="17" r="4" fill={g2} opacity="0.6"/>
    </svg>
  );
}

// ── Fruit pips ────────────────────────────────────────────────────────────────
function FruitPips({ tenses }: { tenses: string[] }) {
  if (tenses.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 5, flexWrap: 'wrap' }}>
      {tenses.slice(0, 6).map(t => {
        const f = TENSE_FRUIT[t];
        if (!f) return null;
        return (
          <div key={t} title={f.label} style={{
            width: 7, height: 7, borderRadius: '50%',
            background: f.color, flexShrink: 0,
          }}/>
        );
      })}
    </div>
  );
}

// ── Water drop badge ──────────────────────────────────────────────────────────
function WaterBadge({ status }: { status: WaterStatus }) {
  if (status === 'fresh') return null;
  const color = status === 'wilting' ? '#ef4444' : '#3b82f6';
  return (
    <div title={status === 'wilting' ? 'Needs water!' : 'Getting thirsty'} style={{
      position: 'absolute', top: 6, left: 6,
      fontSize: 11, lineHeight: 1,
      background: C.surf, borderRadius: 5, padding: '2px 4px',
      border: `1px solid ${color}33`,
      color,
    }}>💧</div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────
type Specimen = {
  term: string;
  meaning: string;
  stage: GrowthStage;
  water: WaterStatus;
  tenses: string[];
  lastSeen: number;
};

// ── Card ──────────────────────────────────────────────────────────────────────
function SpecimenCard({ s }: { s: Specimen }) {
  return (
    <div
      style={{
        borderRadius: 12, background: C.surf, border: `1px solid ${C.border}`,
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        transition: 'transform 0.12s, box-shadow 0.12s', cursor: 'default',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 18px rgba(0,0,0,0.13)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.transform = '';
        (e.currentTarget as HTMLElement).style.boxShadow = '';
      }}
    >
      {/* Icon area */}
      <div style={{
        height: 90, position: 'relative',
        background: 'linear-gradient(140deg, var(--accent-bg) 0%, var(--surf2) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <VocabularyIcon term={s.term} meaning={s.meaning || s.term} size="lg" />
        <WaterBadge status={s.water} />
        {/* Plant stage — bottom-right corner */}
        <div style={{ position: 'absolute', bottom: 4, right: 4 }}>
          <PlantSVG stage={s.stage} water={s.water} />
        </div>
      </div>

      {/* Text + fruit */}
      <div style={{ padding: '8px 8px 9px', textAlign: 'center' }}>
        <div style={{
          fontFamily: AF.h, fontSize: 13, fontWeight: 700, color: C.text,
          lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {s.term}
        </div>
        {s.meaning && (
          <div style={{
            fontFamily: AF.b, fontSize: 10, color: C.muted, marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {s.meaning}
          </div>
        )}
        <FruitPips tenses={s.tenses} />
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ prefix }: { prefix: string }) {
  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10, marginBottom: 14,
      }}>
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} style={{
            borderRadius: 12, border: `1px dashed ${C.border}`, overflow: 'hidden',
            opacity: 0.25 + i * 0.03,
          }}>
            <div style={{ height: 90, background: C.surf2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" opacity="0.4">
                <ellipse cx="16" cy="26" rx="7" ry="2.5" fill="#888"/>
                <line x1="16" y1="25" x2="16" y2="16" stroke="#888" strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M16 19 C16 19 11 16 11 11 C11 11 15 11 16 17" fill="#aaa"/>
                <path d="M16 18 C16 18 21 15 21 10 C21 10 17 10 16 16" fill="#999"/>
              </svg>
            </div>
            <div style={{ padding: '8px 8px 10px' }}>
              <div style={{ height: 8, background: C.border, borderRadius: 4, width: '65%', margin: '0 auto' }}/>
              <div style={{ height: 6, background: C.border, borderRadius: 4, width: '80%', margin: '5px auto 0' }}/>
            </div>
          </div>
        ))}
      </div>
      <a href={`${prefix}/vocabulary`} style={{
        display: 'block', textAlign: 'center', fontFamily: AF.b, fontSize: 13,
        color: 'var(--accent)', textDecoration: 'none',
        padding: '10px', border: '1px dashed var(--accent-border)',
        borderRadius: 10, background: 'var(--accent-bg)',
      }}>
        Practice vocabulary to start catching field specimens →
      </a>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function SpecimenCollection({ prefix }: { prefix: string }) {
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const meta = getVocabMeta();
    const store = readProgress();

    const all: Specimen[] = Object.entries(store.vocabularyByTerm)
      .filter(([, stats]) => stats.correct >= 1)           // 1 correct = in collection
      .map(([term, stats]) => {
        const stage = getGrowthStage(stats);
        const water = getWaterStatus(stats.lastSeen);
        const tenses = getTensesFruit(term);
        return { term, meaning: meta[term] ?? '', stage, water, tenses, lastSeen: stats.lastSeen };
      })
      .sort((a, b) => {
        // Wilting first, then thirsty, then fresh; within each group oldest first
        const wScore = { wilting: 0, thirsty: 1, fresh: 2 };
        const ws = wScore[a.water] - wScore[b.water];
        if (ws !== 0) return ws;
        return a.lastSeen - b.lastSeen;
      })
      .slice(0, 48);

    setSpecimens(all);
    setLoaded(true);
  }, []);

  if (!loaded) return null;

  const wilting = specimens.filter(s => s.water === 'wilting').length;
  const thirsty = specimens.filter(s => s.water === 'thirsty').length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: AF.h, fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: '-0.4px' }}>
            Field Specimens
          </span>
          <span style={{ fontFamily: AF.m, fontSize: 11, color: C.muted }}>
            {specimens.length} caught
          </span>
          {(wilting > 0 || thirsty > 0) && (
            <span style={{ fontFamily: AF.b, fontSize: 11, color: '#3b82f6' }}>
              · {wilting + thirsty} need water
            </span>
          )}
        </div>
        <a href={`${prefix}/vocabulary`} style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', textDecoration: 'none' }}>
          {specimens.length === 0 ? 'Start catching →' : 'See all →'}
        </a>
      </div>

      {specimens.length === 0 ? (
        <EmptyState prefix={prefix} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10 }}>
          {specimens.map(s => <SpecimenCard key={s.term} s={s} />)}
        </div>
      )}
    </div>
  );
}
